import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Navbar.css';
import { AuthContext } from '../Login/AuthContext';
import logo from '../../assets/logo.png';
import flagFR from '../../assets/flags/fr.svg';
import flagGB from '../../assets/flags/gb.svg';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('navbar');
  const currentLang = i18n.language;

  // Fonction pour préfixer les URL avec la langue actuelle si nécessaire
  const getLocalizedPath = (path) => {
    // Si nous sommes sur une page française, ajouter le préfixe /fr aux chemins
    return currentLang === 'fr' ? `/fr${path}` : path;
  };

  // Fonction pour vérifier si un chemin est actif
  const isActive = (path) => {
    // Supprimer le préfixe /fr pour la comparaison
    const normalizedCurrentPath = location.pathname.replace(/^\/fr/, '');
    return normalizedCurrentPath === path;
  };

  // Handle scroll event to add background to navbar on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu when changing routes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Calculate total tokens from user data
  const getUserTotalTokens = () => {
    if (!user) return 0;
    return (user.tokens_ltd || 0) + (user.tokens_subscription || 0);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate(getLocalizedPath('/'));
  };

  // Close menu when clicking on a link or button in mobile view
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Fonction pour naviguer vers une langue spécifique
  const switchLanguage = (lang) => {
    // Ne rien faire si c'est déjà la langue actuelle
    if (lang === currentLang) return;
    
    // Déterminer le chemin équivalent dans l'autre langue
    let newPath = location.pathname;
    if (lang === 'fr') {
      // Si on va vers le français, ajouter le préfixe /fr
      if (!newPath.startsWith('/fr')) {
        newPath = `/fr${newPath}`;
      }
    } else {
      // Si on va vers l'anglais, enlever le préfixe /fr
      newPath = newPath.replace(/^\/fr/, '');
    }
    
    // Naviguer vers le nouveau chemin
    window.location.href = newPath; // Utiliser href pour forcer le rechargement complet
  };

  return (
    <>
      <header className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar__container">
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className="navbar__logo">
            <span className="navbar__logo-text">AiVatar</span>
          </Link>
          
          {/* Navigation Links - Desktop */}
          <nav className={`navbar__navigation ${isMenuOpen ? 'navbar__navigation--active' : ''}`}>
            <ul className="navbar__menu">
              <li className="navbar__menu-item">
                <Link to={getLocalizedPath('/')} className={`navbar__menu-link ${isActive('/') ? 'active' : ''}`} onClick={closeMenu}>
                  {t('home')}
                </Link>
              </li>
              {isAuthenticated && (
                <li className="navbar__menu-item">
                  <Link to="/app" className={`navbar__menu-link ${isActive('/app') ? 'active' : ''}`} onClick={closeMenu}>
                    {t('createAvatar')}
                  </Link>
                </li>
              )}
       
              <li className="navbar__menu-item">
                <Link to={getLocalizedPath('/pricing')} className={`navbar__menu-link ${isActive('/pricing') ? 'active' : ''}`} onClick={closeMenu}>
                  {t('pricing')}
                </Link>
              </li>
            </ul>
            
            {/* Mobile-only user section */}
            <div className="navbar__mobile-auth">
              {isAuthenticated ? (
                <>
                  <div className="navbar__user-email">{user?.email}</div>
                  <div className="navbar__mobile-actions">
                    <div className="navbar__tokens">
                      <span className="navbar__tokens-count">{getUserTotalTokens()}</span>
                      <span className="navbar__tokens-label">{t('tokens')}</span>
                    </div>
                    <button onClick={handleLogout} className="btn btn-outline navbar__logout-btn">
                      {t('logout')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link to={getLocalizedPath('/login')} className="navbar__mobile-login-btn" onClick={closeMenu}>
                    {t('login')}
                  </Link>
                  <Link to={getLocalizedPath('/signup')} className="navbar__mobile-signup-btn" onClick={closeMenu}>
                    {t('signup')}
                  </Link>
                </>
              )}
              
              {/* Sélecteur de langue mobile */}
              <div className="navbar__language-switcher mobile">
                <button 
                  onClick={() => switchLanguage('fr')} 
                  className={`navbar__language-flag ${currentLang === 'fr' ? 'active' : ''}`}
                  aria-label="Français"
                >
                  <img src={flagFR} alt="Français" width="30" height="20" />
                </button>
                <button 
                  onClick={() => switchLanguage('en')} 
                  className={`navbar__language-flag ${currentLang === 'en' ? 'active' : ''}`}
                  aria-label="English"
                >
                  <img src={flagGB} alt="English" width="30" height="20" />
                </button>
              </div>
            </div>
          </nav>
          
          {/* Auth section - Desktop */}
          <div className="navbar__auth">
            {isAuthenticated ? (
              <>
                <div className="navbar__user-section">
                  <div className="navbar__user-email">{user?.email}</div>
                  <div className="navbar__tokens">
                    <span className="navbar__tokens-count">{getUserTotalTokens()}</span>
                    <span className="navbar__tokens-label">{t('tokens')}</span>
                  </div>
                </div>
                <button onClick={handleLogout} className="btn btn-outline navbar__logout-btn">
                  {t('logout')}
                </button>
              </>
            ) : (
              <>
                <Link to={getLocalizedPath('/login')} className="btn btn-outline navbar__login-btn">
                  {t('login')}
                </Link>
                <Link to={getLocalizedPath('/signup')} className="btn btn-primary navbar__signup-btn">
                  {t('signup')}
                </Link>
              </>
            )}
            
            {/* Sélecteur de langue desktop */}
            <div className="navbar__language-switcher desktop">
              <button 
                onClick={() => switchLanguage('fr')} 
                className={`navbar__language-flag ${currentLang === 'fr' ? 'active' : ''}`}
                aria-label="Français"
              >
                <img src={flagFR} alt="Français" width="30" height="20" />
              </button>
              <button 
                onClick={() => switchLanguage('en')} 
                className={`navbar__language-flag ${currentLang === 'en' ? 'active' : ''}`}
                aria-label="English"
              >
                <img src={flagGB} alt="English" width="30" height="20" />
              </button>
            </div>
          </div>
          
          {/* Mobile menu toggle */}
          <button 
            className={`navbar__mobile-toggle ${isMenuOpen ? 'navbar__mobile-toggle--active' : ''}`} 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className="navbar__mobile-toggle-bar"></span>
            <span className="navbar__mobile-toggle-bar"></span>
            <span className="navbar__mobile-toggle-bar"></span>
          </button>
        </div>
      </header>
      
      {/* Overlay for mobile menu */}
      <div 
        className={`navbar__overlay ${isMenuOpen ? 'navbar__overlay--active' : ''}`}
        onClick={toggleMenu}
      ></div>
    </>
  );
};

export default Navbar; 